import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  ExpensesReportData,
  ExpenseCategory,
  ExpenseDetailRow,
  ExpenseStatusSummary,
} from './use-finance-expenses.js';

function fmt(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtPrecise(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function fmtWeek(w: string): string {
  if (!w) return '';
  const d = new Date(w + 'T12:00:00');
  if (isNaN(d.getTime())) return w;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const STATUS_COLOR: Record<string, string> = {
  verified: C.green,
  caution: '#FBBF24',
  flagged: C.red,
  pending: C.textMuted,
};

function StatusPill({ status }: { status: string }) {
  const color = STATUS_COLOR[status] || C.textMuted;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
      color, background: `${color}20`, textTransform: 'uppercase',
    }}>
      {status}
    </span>
  );
}

function CategoryBar({ item, total }: { item: ExpenseCategory; total: number }) {
  const pct = total > 0 ? (item.amount / total) * 100 : 0;
  const statusColor = STATUS_COLOR[item.status] || C.textMuted;
  return (
    <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{item.category}</span>
          <StatusPill status={item.status} />
        </div>
        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{item.count} record{item.count !== 1 ? 's' : ''}</div>
        <div style={{ height: 4, background: C.borderSoft, borderRadius: 2, marginTop: 6, width: '100%' }}>
          <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: statusColor, borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{fmt(item.amount)}</div>
        <div style={{ fontSize: 11, color: C.textMuted }}>{pct.toFixed(1)}%</div>
      </div>
    </div>
  );
}

function StatusSummaryBar({ summary }: { summary: ExpenseStatusSummary }) {
  const entries = [
    { key: 'verified', label: 'Verified', amount: summary.verified },
    { key: 'pending', label: 'Pending', amount: summary.pending },
    { key: 'caution', label: 'Caution', amount: summary.caution },
    { key: 'flagged', label: 'Flagged', amount: summary.flagged },
  ].filter(e => e.amount !== 0);

  if (entries.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      {entries.map((e) => (
        <div key={e.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[e.key] || C.textMuted }} />
          <span style={{ fontSize: 12, color: C.textMuted }}>{e.label}:</span>
          <span style={{ fontSize: 12, color: STATUS_COLOR[e.key] || C.textMuted, fontWeight: 600 }}>{fmt(e.amount)}</span>
        </div>
      ))}
    </div>
  );
}

function DetailTable({ rows }: { rows: ExpenseDetailRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, 10);

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: C.surface }}>
            {['Category', 'Week', 'Amount', 'Status', 'Source', 'Notes'].map(h => (
              <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: C.textMuted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase', borderBottom: `1px solid ${C.borderSoft}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={row.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
              <td style={{ padding: '8px 10px', color: C.text }}>{row.category}</td>
              <td style={{ padding: '8px 10px', color: C.textMuted }}>{fmtWeek(row.week_ending || '')}</td>
              <td style={{ padding: '8px 10px', color: C.text, fontWeight: 600 }}>{fmtPrecise(row.amount)}</td>
              <td style={{ padding: '8px 10px' }}><StatusPill status={row.status} /></td>
              <td style={{ padding: '8px 10px', color: C.textMuted, fontSize: 11 }}>{row.source || '\u2014'}</td>
              <td style={{ padding: '8px 10px', color: C.textMuted, fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.notes || '\u2014'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 10 && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{ marginTop: 8, background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
        >
          {expanded ? 'Show less' : `Show all ${rows.length} rows`}
        </button>
      )}
    </div>
  );
}

interface FinanceExpensesReportProps {
  data: ExpensesReportData;
  onRefresh: () => void;
}

export default function FinanceExpensesReport({ data, onRefresh }: FinanceExpensesReportProps) {
  const { treemap, status_summary, detail, weekly_trend, period } = data;

  const totalExpenses = treemap.reduce((s, c) => s + c.amount, 0);

  const trendChartData = weekly_trend.map((w) => ({
    week: fmtWeek(w.week_ending),
    Expenses: w.total,
  }));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{fmt(totalExpenses)}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Total expenses \u00b7 {period.weeks_included} week{period.weeks_included !== 1 ? 's' : ''}
            \u00b7 {treemap.length} categor{treemap.length !== 1 ? 'ies' : 'y'}
          </div>
        </div>
        <button
          onClick={onRefresh}
          style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      {/* Status summary */}
      <StatusSummaryBar summary={status_summary} />

      {/* Category breakdown */}
      {treemap.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Expenses by category
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {treemap.map((item) => (
              <CategoryBar key={item.category} item={item} total={totalExpenses} />
            ))}
          </div>
        </div>
      )}

      {/* Weekly trend chart */}
      {trendChartData.length > 1 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
            Weekly expense trend
          </div>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendChartData}>
                <CartesianGrid stroke={C.borderSoft} vertical={false} />
                <XAxis dataKey="week" stroke={C.textMuted} tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke={C.textMuted} tickLine={false} axisLine={false} fontSize={11} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 8, fontSize: 12 }}
                  formatter={(value: number) => fmt(value)}
                />
                <Bar dataKey="Expenses" fill={C.red} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Detail table */}
      {detail.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Expense detail ({detail.length} rows)
          </div>
          <DetailTable rows={detail} />
        </div>
      )}
    </div>
  );
}
